const MAX_UPLOAD_DATA_URL_BYTES = 950 * 1024;
const COMPRESSED_IMAGE_TYPE = 'image/webp';
const COMPRESSION_STEPS = [
  { maxDimension: 1800, quality: 0.82 },
  { maxDimension: 1500, quality: 0.76 },
  { maxDimension: 1200, quality: 0.7 },
  { maxDimension: 1000, quality: 0.64 },
  { maxDimension: 800, quality: 0.6 },
];

const SUPPORTED_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
]);

const IMAGE_TYPE_BY_EXTENSION = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
};

const getExtension = (fileName = '') => {
  const match = String(fileName).toLowerCase().match(/\.([a-z0-9]+)$/);
  return match ? match[1] : '';
};

const inferImageType = (file) => {
  const fileType = String(file?.type || '').toLowerCase();
  if (SUPPORTED_IMAGE_TYPES.has(fileType)) {
    return fileType;
  }

  return IMAGE_TYPE_BY_EXTENSION[getExtension(file?.name)] || fileType;
};

export const formatUploadBytes = (bytes = 0) => {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('Failed to read file'));
  reader.onload = () => resolve(String(reader.result || ''));
  reader.readAsDataURL(file);
});

const readBlobAsDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('Failed to read compressed image'));
  reader.onload = () => resolve(String(reader.result || ''));
  reader.readAsDataURL(blob);
});

const dataUrlBytes = (dataUrl) => new Blob([dataUrl]).size;

const normalizeOriginalDataUrl = (dataUrl, mimeType) => {
  if (!mimeType || dataUrl.startsWith(`data:${mimeType};base64,`)) {
    return dataUrl;
  }

  const commaIndex = dataUrl.indexOf(',');
  if (commaIndex === -1) {
    return dataUrl;
  }

  return `data:${mimeType};base64,${dataUrl.slice(commaIndex + 1)}`;
};

const loadImageFromFile = (file) => new Promise((resolve, reject) => {
  const image = new Image();
  const objectUrl = URL.createObjectURL(file);

  image.onload = () => {
    URL.revokeObjectURL(objectUrl);
    resolve(image);
  };
  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error('This image could not be prepared. Please use JPG, PNG, WEBP, or GIF.'));
  };
  image.src = objectUrl;
});

const canvasToBlob = (canvas, type, quality) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (!blob) {
      reject(new Error('Failed to compress image'));
      return;
    }

    resolve(blob);
  }, type, quality);
});

const resizeImage = async (file, maxDimension, quality) => {
  const image = await loadImageFromFile(file);
  const scale = Math.min(1, maxDimension / Math.max(image.naturalWidth || image.width, image.naturalHeight || image.height));
  const width = Math.max(1, Math.round((image.naturalWidth || image.width) * scale));
  const height = Math.max(1, Math.round((image.naturalHeight || image.height) * scale));
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Failed to prepare image for upload');
  }

  canvas.width = width;
  canvas.height = height;
  context.drawImage(image, 0, 0, width, height);

  const blob = await canvasToBlob(canvas, COMPRESSED_IMAGE_TYPE, quality);
  return readBlobAsDataUrl(blob);
};

export const prepareImageForUpload = async (file) => {
  if (!file) {
    throw new Error('Choose an image first.');
  }

  const mimeType = inferImageType(file);
  if (!SUPPORTED_IMAGE_TYPES.has(mimeType)) {
    throw new Error('Please upload a JPG, PNG, WEBP, or GIF image.');
  }

  const originalDataUrl = normalizeOriginalDataUrl(await readFileAsDataUrl(file), mimeType);
  if (dataUrlBytes(originalDataUrl) <= MAX_UPLOAD_DATA_URL_BYTES) {
    return {
      fileName: file.name || 'image',
      base64: originalDataUrl,
    };
  }

  for (const step of COMPRESSION_STEPS) {
    const compressedDataUrl = await resizeImage(file, step.maxDimension, step.quality);
    if (dataUrlBytes(compressedDataUrl) <= MAX_UPLOAD_DATA_URL_BYTES) {
      return {
        fileName: file.name || 'image',
        base64: compressedDataUrl,
      };
    }
  }

  const fallbackDataUrl = await resizeImage(file, 700, 0.56);
  if (dataUrlBytes(fallbackDataUrl) > MAX_UPLOAD_DATA_URL_BYTES) {
    throw new Error(`Photo is still too large after compression (${formatUploadBytes(file.size)}). Try a smaller image.`);
  }

  return {
    fileName: file.name || 'image',
    base64: fallbackDataUrl,
  };
};

export const getUploadErrorMessage = (error, fallback = 'Upload failed') => {
  const responseMessage = error?.response?.data?.message;
  if (typeof responseMessage === 'string' && responseMessage.trim()) {
    return responseMessage;
  }

  if (error?.response?.status === 413) {
    return 'Photo is too large. Try a smaller image, or upload one photo at a time.';
  }

  if (error?.response?.status >= 500) {
    return 'Upload failed on the server. Please try again with a smaller image.';
  }

  if (error?.code === 'ERR_NETWORK') {
    return 'Upload server could not be reached. Please check that the backend is running.';
  }

  if (error?.message) {
    return error.message;
  }

  return `${fallback}. Try a JPG, PNG, WEBP, or GIF photo.`;
};
